/**
 * SelfHostSecretModal (OCP-444) - reveal-once display of a self-host tenant's
 * beacon secret + the telemetry env block the operator pastes on the instance.
 *
 * The plaintext secret is returned by the API exactly once (register or
 * rotate); this modal makes that explicit and gives one-click copy of the env
 * snippet. Once closed, the secret is gone - rotate to get a new one.
 */
import { useTransientState } from '../lib/useTransientState';

export interface SelfHostSecretReveal {
  beaconSecret: string;
  onboarding: {
    telemetry_endpoint: string;
    env: Record<string, string>;
  };
  subdomain: string;
  selfHostUrl: string | null;
  /** true = rotation (invalidated the previous secret), false = fresh register. */
  rotated?: boolean;
}

export function SelfHostSecretModal({
  reveal,
  onClose,
}: {
  reveal: SelfHostSecretReveal;
  onClose: () => void;
}) {
  const [copied, setCopied] = useTransientState<'env' | 'secret' | null>(null, 2000);

  const envBlock = Object.entries(reveal.onboarding.env)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');

  const copy = async (what: 'env' | 'secret', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      /* clipboard blocked - operator can select manually */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {reveal.rotated ? 'Beacon secret rotated' : 'Self-host instance registered'}
        </h2>
        <p className="mt-0.5 font-mono text-xs text-gray-500">
          {reveal.selfHostUrl ?? reveal.subdomain}
        </p>
        <p className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          The beacon secret is shown <strong>once</strong> - copy it now.
          {reveal.rotated
            ? ' The previous secret is now invalid; update the instance before the next beacon.'
            : ' Paste it as TELEMETRY_SHARED_SECRET on the instance and redeploy.'}
        </p>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Instance environment
            </span>
            <button
              type="button"
              onClick={() => void copy('env', envBlock)}
              className="rounded bg-brand-500 px-2 py-1 text-xs font-medium text-white hover:bg-brand-600"
            >
              {copied === 'env' ? 'Copied' : 'Copy env'}
            </button>
          </div>
          <pre className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-xs">
            {envBlock}
          </pre>
        </div>

        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          Set these on the self-host instance (env / compose), redeploy, and the next beacon (every 30 minutes, hourly on core releases before 0.186)
          lands as a telemetry snapshot on this tenant. Telemetry endpoint:{' '}
          <code className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-xs">
            {reveal.onboarding.telemetry_endpoint}
          </code>
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void copy('secret', reveal.beaconSecret)}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {copied === 'secret' ? 'Copied secret' : 'Copy secret only'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
